import {
  Box,
  Card as JoyCard,
  CardContent,
  CardOverflow,
  Tooltip,
  TypographyProps,
} from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { omit } from 'lodash';
import { ReactNode } from 'react';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { FlexBox, TextEllipsis } from 'core/elements';
import { theme } from 'theme';
import { Color, Level, Orientation, Variant } from 'theme/enums';

import { ISubtitleCard } from './type';

interface IProps extends ISubtitleCard {
  options?: {
    cardSx?: SxProps;
    contentSx?: SxProps;
    labelProps?: TypographyProps;
    valueProps?: TypographyProps;
    valueElement?: ReactNode;
  };
}

export const SubtitleCard = ({
  label,
  subtitle,
  value,
  valueTooltip,
  extra,
  extraTooltip,
  icon = null,
  hide,
  options,
  buttons,
  footer,
}: IProps) => {
  if (hide) {
    return null;
  }

  const valueContent = options?.valueElement ?? (
    <TextEllipsis
      content={value || DEFAULT_PLACEHOLDER}
      level={Level.TITLE_MD}
      maxRows={1}
      {...omit(options?.valueProps, 'lineHeight')}
      sx={{ fontWeight: 700, ...options?.valueProps?.sx }}
    />
  );

  const extraContent = (
    <TextEllipsis content={extra} level={Level.BODY_XS} maxRows={1} defaultPlaceholder="" />
  );

  return (
    <JoyCard
      variant={Variant.PLAIN}
      orientation={Orientation.HORIZONTAL}
      sx={{ border: 0, flex: 1, display: 'flex', width: '100%', ...options?.cardSx }}
    >
      {icon && (
        <CardOverflow sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FlexBox
            sx={{
              justifyContent: 'center',
              width: 42,
              height: 42,
              borderRadius: 6,
              backgroundColor: theme.palette.custom.cloud,
              margin: '8px',

              '& svg': {
                width: '32px !important',
                height: '32px !important',
                color: theme.palette.custom.velvet,
              },
            }}
          >
            {icon}
          </FlexBox>
        </CardOverflow>
      )}
      <CardContent sx={{ flex: 1, ...options?.contentSx }}>
        <Box pb={1}>
          <TextEllipsis
            content={label}
            maxRows={1}
            color={Color.NEUTRAL}
            level={Level.BODY_XS}
            {...omit(options?.labelProps, 'lineHeight')}
            sx={{ fontWeight: 600, ...options?.labelProps?.sx }}
          />
          {subtitle && (
            <TextEllipsis
              content={subtitle}
              maxRows={1}
              color={Color.NEUTRAL}
              level={Level.BODY_XS}
            />
          )}
        </Box>
        <FlexBox>
          {valueTooltip ? (
            <Tooltip title={valueTooltip} arrow>
              <Box sx={{ minWidth: 0, flex: 1 }}>{valueContent}</Box>
            </Tooltip>
          ) : (
            valueContent
          )}
          {extraTooltip ? (
            <Tooltip title={extraTooltip} arrow>
              <Box>{extraContent}</Box>
            </Tooltip>
          ) : (
            extraContent
          )}
        </FlexBox>
        {footer}
      </CardContent>
      {buttons?.length > 0 && (
        <CardOverflow
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
        >
          {buttons}
        </CardOverflow>
      )}
    </JoyCard>
  );
};
